/**
 * Copyright ©2022 Drivepoint
 */

import LRU from "lru-cache";
import {Config} from "@bainbridge-growth/node-frontend";
import EventStreamer from "@utilities/EventStreamer";
import WebAppServerError from "../../errors/WebAppServerError";
import Firebase from "../firebase/Firebase";

export default class WebAppServerClient {

  private static _cache?: any = new LRU({max: 20, ttl: 60000});

  static clear(): void {
    WebAppServerClient._cache.clear();
  }

  static async getStandardOptions(method: string, body?: any): Promise<any> {
    const token = await Firebase.auth?.currentUser?.getIdToken();
    if (body !== undefined) {
      body = typeof body === "string" ? body : JSON.stringify(body);
    }
    return {
      headers: {
        "x-drivepoint-frontend": "webapp-mui",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      method: method,
      body: body
    };
  }

  static async request(path: string, method: string, body?: any, abortController?: AbortController, extraOptions?: {}): Promise<any> {
    const response = await WebAppServerClient._request(path, method, body, abortController, extraOptions);
    return response.json();
  }

  // For endpoints that respond with 204 No Content (e.g. DELETE). Calling the
  // regular `request()` against these throws when `response.json()` tries to
  // parse an empty body, so this variant resolves on success without parsing.
  static async requestNoContent(path: string, method: string, body?: any, abortController?: AbortController, extraOptions?: {}): Promise<void> {
    await WebAppServerClient._request(path, method, body, abortController, extraOptions);
  }

  static async stream(path: string, method: string, body?: any, abortController?: AbortController, extraOptions?: {}): Promise<EventStreamer> {
    const response = await WebAppServerClient._request(path, method, body, abortController, extraOptions);
    if (!response.body) { throw new WebAppServerError(response.statusText, path, {}, response.status); }
    return new EventStreamer(response.body.getReader());
  }

  private static async _request(path: string, method: string, body?: any, abortController?: AbortController, extraOptions?: {}): Promise<Response> {
    let options = await this.getStandardOptions(method, body);
    options.signal = abortController?.signal;
    if (extraOptions) { options = {...options, ...extraOptions}; }
    const response = await fetch(`${Config.get("server.api.url", window.location.origin)}${path}`, options);
    if (!response.ok) {
      throw new WebAppServerError(response.statusText, path, await this.readErrorResponse(response), response.status);
    }
    return response;
  }

  private static async readErrorResponse(response: Response): Promise<any> {
    const responseText = await response.text();
    if (!responseText.trim()) { return {}; }
    try {
      return JSON.parse(responseText);
    } catch {
      return {error: responseText};
    }
  }

  static async signUp(data: any): Promise<any> {
    return this.request("/ui/auth/sign-up", "POST", data);
  }

  static async applyInvitationFeatures(token: string): Promise<any> {
    return this.request("/ui/auth/invitation/features", "POST", {token});
  }

  static async applyInvitationGroups(token: string): Promise<any> {
    return this.request("/ui/auth/invitation/groups", "POST", {token});
  }

  static async applyInvitationResources(token: string): Promise<any> {
    return this.request("/ui/auth/invitation/resources", "POST", {token});
  }

  static async inviteCompany(data: any): Promise<any> {
    return this.request("/ui/company/invite", "POST", data);
  }

  static async sendForgotPassword(data: any): Promise<any> {
    return this.request("/ui/auth/forgot-password", "POST", data);
  }

  static async clearPasswordReset(data: any): Promise<void> {
    return this.request("/ui/auth/clear-reset", "POST", data);
  }

  static async inviteUser(companyId: string, data: any): Promise<any> {
    return this.request(`/ui/company/${companyId}/invite-user`, "POST", data);
  }

  static async resendUserInvite(companyId: string, data: any): Promise<any> {
    return this.request(`/ui/company/${companyId}/resend-user-invitation`, "POST", data);
  }

  static async createUserInviteLink(companyId: string, data: {email: string; callback_url: string}): Promise<{link: string}> {
    return this.request(`/ui/company/${companyId}/user-invitation-link`, "POST", data);
  }

  static async removeUserInvite(companyId: string, id: string): Promise<any> {
    return this.request(`/ui/company/${companyId}/user-invitation/${id}`, "DELETE");
  }

  static async deleteCompanyUser(companyId: string, email: string): Promise<any> {
    return this.request(`/api/company/${companyId}/user/${email}/company`, "DELETE");
  }

  static async listPermissionGroups(companyId: string): Promise<any> {
    return this.request(`/ui/company/${companyId}/permission_groups`, "GET");
  }

  static async createPermissionGroup(companyId: string, group: any): Promise<any> {
    return this.request(`/ui/company/${companyId}/permission_groups`, "POST", group);
  }

  static async updatePermissionGroup(companyId: string, group: any): Promise<any> {
    return this.request(`/ui/company/${companyId}/permission_groups/${group.id}`, "PATCH", group);
  }

  static async deletePermissionGroup(companyId: string, id: string): Promise<void> {
    return this.requestNoContent(`/ui/company/${companyId}/permission_groups/${id}`, "DELETE");
  }

  static async getPlanAccess(companyId: string, planId: string): Promise<any> {
    return this.request(`/api/company/${companyId}/plan/${planId}/access`, "GET");
  }

  static async updatePlanAccess(companyId: string, planId: string, grants: any): Promise<any> {
    return this.request(`/api/company/${companyId}/plan/${planId}/access`, "PATCH", {grants});
  }

  static async getReportAccess(companyId: string, reportId: string): Promise<any> {
    return this.request(`/api/company/${companyId}/reportDefinitions/${reportId}/access`, "GET");
  }

  static async updateReportAccess(companyId: string, reportId: string, grants: any): Promise<any> {
    return this.request(`/api/company/${companyId}/reportDefinitions/${reportId}/access`, "PATCH", {grants});
  }

  static async syncMicrosoftGrants(): Promise<any> {
    return this.request("/ui/drivepoint_user/microsoft_sync", "POST");
  }

  static async getUserFeaturesAccess(companyId: string, id: string): Promise<any> {
    return this.request(`/api/company/${companyId}/user/${id}/featuresAccess`, "GET");
  }

  static async updateUserFeaturesAccess(companyId: string, id: string, features: any): Promise<any> {
    return this.request(`/api/company/${companyId}/user/${id}/featuresAccess`, "PATCH", features);
  }

  static async getMcpServerSettings(companyId: string): Promise<any> {
    return this.request(`/api/company/${companyId}/mcpServerSettings`, "GET");
  }

  static async getMcpTools(companyId: string): Promise<any> {
    return this.request(`/api/company/${companyId}/mcpServerSettings/tools`, "GET");
  }

  static async getUserMcpToolsAccess(companyId: string, id: string): Promise<any> {
    return this.request(`/api/company/${companyId}/user/${id}/mcpToolsAccess`, "GET");
  }

  static async updateUserMcpToolsAccess(companyId: string, id: string, allowedTools: string[]): Promise<any> {
    return this.request(`/api/company/${companyId}/user/${id}/mcpToolsAccess`, "PATCH", {allowedTools});
  }

  static async getMcpDatasets(companyId: string): Promise<any> {
    return this.request(`/api/company/${companyId}/mcpServerSettings/datasets`, "GET");
  }

  static async getUserMcpDatasetsAccess(companyId: string, id: string): Promise<any> {
    return this.request(`/api/company/${companyId}/user/${id}/mcpDatasetsAccess`, "GET");
  }

  static async updateUserMcpDatasetsAccess(companyId: string, id: string, allowedDatasets: string[], allowedTables: Record<string, string[]> = {}): Promise<any> {
    return this.request(`/api/company/${companyId}/user/${id}/mcpDatasetsAccess`, "PATCH", {allowedDatasets, allowedTables});
  }

  static async getDazzlerAccess(companyId: string, id: string): Promise<any> {
    return this.request(`/api/company/${companyId}/dazzler/${id}/access`, "GET");
  }

  static async updateDazzlerAccess(companyId: string, id: string, grants: any): Promise<any> {
    return this.request(`/api/company/${companyId}/dazzler/${id}/access`, "PATCH", {grants});
  }

  static async getUrl(url: string): Promise<any> {
    const options = await this.getStandardOptions("GET");
    return fetch(url, options)
      .then(async (response: any) => {
        const data = await response.json();
        if (response.status !== 200) { throw new Error(data.error); }
        return data;
      });
  }

  static async getModelSchema(model: string): Promise<any> {
    return this.request(`/ui/${model}/MODEL-SCHEMA`, "GET");
  }

  static async listModels(model: string, all: boolean = false): Promise<any> {
    return this.request(`/ui/${model}?all=${all}`, "GET");
  }

  static async createModel(model: string, data: any): Promise<any> {
    return this.request(`/ui/${model}`, "POST", data);
  }

  static async updateModel(model: string, data: any): Promise<any> {
    return this.request(`/ui/${model}/${data.id}`, "PATCH", data);
  }

  static async deleteModel(model: string, data: any): Promise<any> {
    return this.request(`/ui/${model}/${data.id}`, "DELETE", data);
  }

  static async createCompanyPage(companyId: string, page: any): Promise<any> {
    return this.request(`/ui/navigationPage/${companyId}`, "POST", page);
  }

  static async updateCompanyPage(companyId: string, page: any): Promise<any> {
    return this.request(`/ui/navigationPage/${companyId}/${page.id}`, "PATCH", page);
  }

  static async deleteCompanyPage(companyId: string, pageId: string): Promise<any> {
    return this.request(`/ui/navigationPage/${companyId}/${pageId}`, "DELETE");
  }

  static async connectIntuit(companyId: string): Promise<any> {
    return this.request(`/ui/intuit/connect/${companyId}`, "GET");
  }

  static async connectSalesforce(
    companyId: string | undefined,
    body: {isSandbox?: boolean; returnUrl?: string; redirectUri?: string} = {}
  ): Promise<any> {
    return this.request(`/ui/salesforce/connect/${companyId}`, "POST", body);
  }

  static async createSalesforceAirbyteConnection(
    companyId: string | undefined,
    orgId: string,
    body: {customObjects?: string[]; startDate?: string; companyStoresName?: string} = {}
  ): Promise<any> {
    return this.request(`/ui/salesforce/createAirbyteConnection/${companyId}/${orgId}`, "POST", body);
  }

  static async listSalesforceOrgs(companyId: string | undefined): Promise<any> {
    return this.request(`/ui/salesforce/orgs/${companyId}`, "GET");
  }

  static async listSalesforceCustomObjects(companyId: string | undefined, orgId: string): Promise<Array<{name: string; label: string}>> {
    return this.request(`/ui/salesforce/orgs/${companyId}/${orgId}/objects`, "GET");
  }

  static async updateSalesforceOrg(
    companyId: string | undefined,
    orgId: string,
    body: {customObjects?: string[]; startDate?: string}
  ): Promise<any> {
    return this.request(`/ui/salesforce/orgs/${companyId}/${orgId}`, "PATCH", body);
  }

  static async disconnectIntuit(companyId: string): Promise<any> {
    return this.request(`/ui/intuit/disconnect/${companyId}`, "GET");
  }

  static async requestIntuitDisconnect(data: any): Promise<any> {
    return this.request("/ui/intuit/disconnect_request", "POST", data);
  }

  static async connectAmazonSP(companyId: string, redirectUri: string): Promise<any> {
    return this.request(`/ui/amazonSP/connect/${companyId}`, "POST", {redirectUri});
  }

  static async requestAmazonSPRefreshToken(companyId: string, oAuthCode: string, sellingPartnerId: string): Promise<any> {
    return this.request(`/ui/amazonSP/requestRefreshToken/${companyId}`, "POST", {oAuthCode, sellingPartnerId});
  }

  static async connectAmazonVendor(companyId: string, redirectUri: string): Promise<any> {
    return this.request(`/ui/amazonVendor/connect/${companyId}`, "POST", {redirectUri});
  }

  static async requestAmazonVendorRefreshToken(companyId: string, oAuthCode: string, vendorGroupId: string): Promise<any> {
    return this.request(`/ui/amazonVendor/requestRefreshToken/${companyId}`, "POST", {oAuthCode, vendorGroupId});
  }

  static async requestTiktokMarketingAccessToken(companyId: string, authCode: string): Promise<any> {
    return this.request(`/ui/tiktokMarketing/requestAccessToken/${companyId}`, "POST", {authCode});
  }

  static async onboardTiktokMarketing(companyId: string, data?: {commitToDWH?: boolean; sourceConfig?: object}): Promise<any> {
    return this.request(`/api/airbyteOss/tiktokMarketing/${companyId}/onboard`, "POST", data);
  }

  static async listTiktokMarketingAdvertisers(companyId: string): Promise<Array<{advertiserId: string; advertiserName: string; country?: string; currency?: string}>> {
    return this.request(`/ui/tiktokMarketing/advertisers/${companyId}`, "GET");
  }

  static async connectNetsuite(companyId: string, data: object): Promise<any> {
    return this.request(`/ui/netsuite/connect/${companyId}`, "POST", data);
  }

  static async getNetsuiteEmail(companyId: string): Promise<string> {
    const response = await this.request(`/ui/netsuite/email/${companyId}`, "GET");
    return response.emailAddress;
  }

  static async connectNetsuiteApi(companyId: string, data: {
    accountId: string;
    consumerKey: string;
    consumerSecret: string;
    tokenId: string;
    tokenSecret: string;
    startDate?: string;
    objectTypes?: string[];
  }): Promise<any> {
    const body: Record<string, unknown> = {
      accountId: data.accountId,
      consumerKey: data.consumerKey,
      consumerSecret: data.consumerSecret,
      tokenId: data.tokenId,
      tokenSecret: data.tokenSecret
    };
    if (data.startDate) { body.startDate = data.startDate; }
    if (data.objectTypes?.length) { body.objectTypes = data.objectTypes; }
    return this.request(`/ui/netsuite-api/connect/${companyId}`, "POST", body);
  }

  static async listNetsuiteApiStores(companyId: string): Promise<any> {
    const connection = await this.request(`/ui/netsuite-api/connection/${companyId}`, "GET");
    return connection?.id ? [connection] : [];
  }

  static async disconnectNetsuiteApi(companyId: string, storeId: string): Promise<any> {
    return this.requestNoContent(`/ui/dataSources/${companyId}/netsuite_api/${storeId}`, "DELETE");
  }

  // Connects a "CustomFinancials" source: the server provisions a dedicated GCS bucket
  // ("bbcust-<companyId>-raw-financial-reports") for the customer's financial reports and
  // returns { serviceAccount } — the service account the customer uses to deliver reports.
  static async connectFinancials(companyId: string, data: object): Promise<any> {
    return this.request(`/ui/customFinancials/connect/${companyId}`, "POST", data);
  }

  // Retrieves the CustomFinancials integration config for a company (returns { serviceAccount, bucket }).
  static async getFinancialsConfig(companyId: string): Promise<any> {
    return this.request(`/ui/customFinancials/config/${companyId}`, "GET");
  }

  // Reloads the customer's financial reports from their GCS bucket. Called before a
  // data refresh so the pipeline picks up the latest files delivered to the bucket.
  static async reloadFinancials(companyId: string): Promise<any> {
    return this.request(`/ui/customFinancials/reload/${companyId}`, "POST");
  }

  static async refreshData(companyId: string, data?: object): Promise<any> {
    return this.request(`/api/airflow/dags/${companyId}/run`, "POST", data);
  }

  static async getLastRefreshDataInfo(companyId: string, dataSourceId?: string): Promise<any> {
    return this.request(`/api/airflow/dags/${companyId}/lastRun/${dataSourceId}`, "GET");
  }

  static async getLastCompletedRefreshDataInfo(companyId: string, dataSourceId = "ALL"): Promise<any> {
    return this.request(`/api/airflow/dags/${companyId}/lastCompletedRun/${dataSourceId}`, "GET");
  }
}
